import {
  Award, Image, Camera, Target, BarChart2, Inbox, CreditCard, Crosshair, PieChart, Users,
  BrainCircuit, Mic, Sunrise, PenLine, Share2, Kanban, ArrowUpRight,
} from "lucide-react";
import { Reveal } from "./Reveal";
import { FEATURES, CLUSTERS } from "@/data/content";

const ICONS = {
  Award, Image, Camera, Target, BarChart2, Inbox, CreditCard, Crosshair, PieChart, Users,
  BrainCircuit, Mic, Sunrise, PenLine, Share2, Kanban,
};

const groupByCluster = () => {
  const map = { A: [], B: [], C: [], D: [] };
  FEATURES.forEach((f) => { if (map[f.cluster]) map[f.cluster].push(f); });
  return map;
};

const Card = ({ f, index }) => {
  const Icon = ICONS[f.icon] || Award;
  return (
    <Reveal delay={(index % 3) * 0.08}>
      <div
        className="group relative h-full p-8 lg:p-10 border-r border-b border-ink/10 hover:bg-ink transition-colors duration-500"
        data-testid={`feature-card-${index}`}
      >
        <div className="flex items-start justify-between mb-8">
          <div className="w-11 h-11 flex items-center justify-center border border-ink/15 group-hover:border-bone/30 transition-colors duration-500">
            <Icon className="w-5 h-5 text-coral" />
          </div>
          <span className="font-serif text-2xl text-ink/20 group-hover:text-bone/30 transition-colors duration-500">
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>
        <h3 className="font-serif text-2xl md:text-3xl text-ink group-hover:text-bone transition-colors duration-500">
          {f.title}
        </h3>
        <p className="mt-2 text-sm font-medium text-coral">{f.line}</p>
        <p className="mt-3 text-sm text-ink/60 group-hover:text-bone/70 leading-relaxed transition-colors duration-500">
          {f.body}
        </p>
        <ArrowUpRight className="mt-6 w-5 h-5 text-ink/30 group-hover:text-coral transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </div>
    </Reveal>
  );
};

export const FeatureGrid = () => {
  const grouped = groupByCluster();
  let idx = 0;
  return (
    <section id="features" className="py-24 md:py-32 border-t border-ink/10" data-testid="features-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="max-w-3xl mb-16 md:mb-24">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] text-coral mb-4">Sixteen features · Four clusters</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink">
              Everything the concierge <span className="italic text-coral">quietly runs</span> in the background.
            </h2>
            <p className="mt-6 text-base md:text-lg text-ink/70">
              One chat. Four clusters. Sixteen tools your competitors can&apos;t match — all in one app.
            </p>
          </Reveal>
        </div>

        <div className="space-y-16 md:space-y-24" data-testid="feature-clusters">
          {CLUSTERS.map((c) => (
            <div key={c.id} id={`cluster-${c.id.toLowerCase()}`} className="scroll-mt-40" data-testid={`cluster-${c.id.toLowerCase()}`}>
              <Reveal>
                <div className="max-w-2xl mb-10 md:mb-12">
                  <div className="flex items-baseline gap-4 mb-3">
                    <span className="font-serif text-6xl md:text-7xl leading-none text-coral/30">{c.id}</span>
                    <p className="text-xs uppercase tracking-[0.2em] text-ink/50">{c.eyebrow}</p>
                  </div>
                  <h3 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
                    {c.title}
                  </h3>
                  <p className="mt-4 text-sm md:text-base text-ink/60 leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-l border-t border-ink/10">
                {grouped[c.id].map((f) => {
                  const i = idx++;
                  return <Card key={f.title} f={f} index={i} />;
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
